import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, CheckCircle, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useLeadEscalations } from "@/hooks/useLeadEscalations";
import { dispatchConversationInboxBadgeSync } from "@/lib/conversation-inbox-patch";

interface EscalationEvent {
  id: string;
  escalation_type: string;
  error_details: any;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
}

interface EscalationHistoryProps {
  leadId: string;
  workspaceId?: string | null;
  conversationId?: string | null;
  memberId?: string | null;
  onResolve?: () => void;
  onEscalationResolved?: (result: import("@/lib/resolve-escalation-followup").ResolveEscalationResult) => void | Promise<void>;
}

const escalationTypeLabels: Record<string, string> = {
  tool_error: "Erro de Ferramenta",
  complaint: "Reclamação",
  manual: "Manual",
  human_handoff: "Escalado pelo agente",
  agent_handoff: "Transferido para outro agente",
  bot_loop: "Loop bot/IA detectado",
};

const escalationTypeColors: Record<string, string> = {
  tool_error: "destructive",
  complaint: "destructive",
  manual: "secondary",
  human_handoff: "secondary",
  agent_handoff: "secondary",
  bot_loop: "destructive",
};

const toolLabels: Record<string, string> = {
  create_calendar_event: "Agendar reunião",
  update_calendar_event: "Atualizar agendamento",
  delete_calendar_event: "Cancelar agendamento",
  get_service_availability: "Consultar horários",
  search_products: "Buscar produtos",
  send_catalog: "Enviar catálogo",
};

const friendlyErrorByType: Record<string, string> = {
  date_outside_offered_slots:
    "O agente tentou agendar em um horário fora dos disponíveis na agenda.",
  event_overlap: "Horário indisponível: já existe um compromisso nesse intervalo.",
  google_auth_needs_reconnect:
    "A integração com o Google Calendar precisa ser reconectada.",
  token_refresh_in_progress:
    "A sincronização com o Google Calendar estava em andamento. Já foi retomada.",
  ambiguous_match: "Mais de um evento similar foi encontrado na agenda.",
  slot_busy: "Horário ocupado na agenda no momento da tentativa.",
};

function humanizeErrorDetails(details: any): string | null {
  if (!details) return null;
  if (details.error_type && friendlyErrorByType[details.error_type]) {
    return friendlyErrorByType[details.error_type];
  }
  const raw = typeof details.error === "string" ? details.error : "";
  const lower = raw.toLowerCase();
  if (lower.includes("non-2xx") || lower.includes("edge function")) {
    return "Não foi possível concluir a ação automatizada. Tente novamente.";
  }
  if (lower.includes("timeout")) {
    return "A ação demorou demais para responder. Tente novamente.";
  }
  if (lower.includes("network") || lower.includes("fetch failed")) {
    return "Falha temporária de conexão durante a ação.";
  }
  if (raw && raw.length < 140 && !/[{}<>]/.test(raw)) {
    return raw;
  }
  return "Ocorreu uma falha ao executar a ação automatizada.";
}

export function EscalationHistory({
  leadId,
  workspaceId,
  conversationId,
  memberId,
  onResolve,
  onEscalationResolved,
}: EscalationHistoryProps) {
  const [events, setEvents] = useState<EscalationEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [reactivateAgent, setReactivateAgent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { pendingCount, resolveAll } = useLeadEscalations(leadId);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from("lead_escalation_events")
        .select("*")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error("Error fetching escalation events:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    void fetchEvents();
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return;
    const channelName = `escalation-history-${leadId}`;
    const channel = subscribePostgresChannel(
      channelName,
      {
        event: "*",
        schema: "public",
        table: "lead_escalation_events",
        filter: `lead_id=eq.${leadId}`,
      },
      () => {
        void fetchEvents();
      },
    );
    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [leadId]);

  const handleResolve = async () => {
    setSubmitting(true);
    const result = await resolveAll({
      reactivateAgent,
      conversationId,
      memberId,
      workspaceId,
    });
    setSubmitting(false);

    if (!result.ok) {
      toast({
        title: "Erro",
        description: result.error || "Não foi possível resolver a escalação.",
        variant: "destructive",
      });
      return;
    }

    if (conversationId) {
      dispatchConversationInboxBadgeSync({ conversationId });
    }

    let description = "O problema foi marcado como resolvido.";
    if (result.stageRestored) {
      description += " Lead devolvido à coluna anterior.";
    }
    if (result.agentReactivated) {
      description += " Agente de IA reativado.";
    } else if (result.agentBlocked) {
      description += " IA não reativada: estágio bloqueia o agente.";
    } else if (result.agentError) {
      description += ` IA não reativada: ${result.agentError}`;
    }

    toast({
      title: "Escalação resolvida",
      description,
    });

    setResolveDialogOpen(false);
    setReactivateAgent(true);
    await fetchEvents();
    onResolve?.();

    if (onEscalationResolved) {
      await onEscalationResolved(result);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Histórico de Escalações</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Carregando...</p>
        </CardContent>
      </Card>
    );
  }

  if (events.length === 0) {
    return null;
  }

  return (
    <>
      <Card className="border-orange-200 dark:border-orange-900">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            Histórico de Escalações
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {events.map((event) => (
            <div key={event.id} className="space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant={escalationTypeColors[event.escalation_type] as any}>
                      {escalationTypeLabels[event.escalation_type]}
                    </Badge>
                    {event.resolved_at && (
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Resolvido
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    {format(new Date(event.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </div>

                  {event.escalation_type === "tool_error" && event.error_details?.tool && (
                    <p className="text-xs text-muted-foreground">
                      Ação: <span className="font-medium">{toolLabels[event.error_details.tool] ?? event.error_details.tool}</span>
                    </p>
                  )}

                  {event.escalation_type === "complaint" && event.error_details?.message && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      "{event.error_details.message}"
                    </p>
                  )}

                  {event.escalation_type === "human_handoff" && event.error_details?.reason && (
                    <p className="text-xs text-muted-foreground line-clamp-3">
                      {event.error_details.reason}
                    </p>
                  )}

                  {event.escalation_type === "agent_handoff" && (
                    <p className="text-xs text-muted-foreground line-clamp-3">
                      {event.error_details?.target_agent_name
                        ? `Transferido para ${event.error_details.target_agent_name}`
                        : event.error_details?.reason || "Transferência entre agentes IA"}
                    </p>
                  )}

                  {humanizeErrorDetails(event.error_details) &&
                    event.escalation_type !== "human_handoff" &&
                    event.escalation_type !== "agent_handoff" && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {humanizeErrorDetails(event.error_details)}
                    </p>
                  )}

                  {event.resolved_at && (
                    <p className="text-xs text-green-600 dark:text-green-400">
                      Resolvido em {format(new Date(event.resolved_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </p>
                  )}
                </div>

                {!event.resolved_at && pendingCount > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setResolveDialogOpen(true)}
                    className="shrink-0"
                  >
                    Marcar Resolvido
                  </Button>
                )}
              </div>

              {event !== events[events.length - 1] && <Separator />}
            </div>
          ))}
        </CardContent>
      </Card>

      <AlertDialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar escalação como resolvida?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingCount === 1
                ? "A escalação pendente deste lead será marcada como resolvida."
                : `As ${pendingCount} escalações pendentes deste lead serão marcadas como resolvidas.`}
            </AlertDialogDescription>
            {conversationId ? (
              <div className="flex items-start gap-2 rounded-md border p-3">
                <Checkbox
                  id="reactivate-agent-escalation-history"
                  checked={reactivateAgent}
                  onCheckedChange={(checked) => setReactivateAgent(checked === true)}
                  disabled={submitting}
                />
                <div className="space-y-1">
                  <Label htmlFor="reactivate-agent-escalation-history" className="text-sm font-medium leading-none">
                    Reativar agente de IA
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Retoma respostas automáticas nesta conversa após resolver.
                  </p>
                </div>
              </div>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleResolve()} disabled={submitting}>
              {submitting ? "Resolvendo..." : "Resolver"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
